import { oneLine } from 'common-tags';

import { Info } from '@/icons';
import { NextLink, Notifications } from '@/atoms';
import { BundleOptions, Button, SellerBox, ViewMore } from '@/molecules';
import { ProductForms } from '@/organisms/product';

import { routes } from '@/lib/routes';
import { formatDateInterval, isOutOfStock, removeBaseURL } from '@/lib/utils';

import type { FunctionComponent } from 'react';
import type { TypeProductDescriptionProps } from './types';

// TODO: Añadir soporte para distintos templates
/**
 * ProductDescription
 */
export const ProductDescription: FunctionComponent<
  TypeProductDescriptionProps
> = ({
  className = '',
  category = '',
  categoryLink = '',
  name,
  productId,
  description = '',
  price,
  regularPrice,
  moreInfo,
  attributes,
  type = 'simple',
  variations,
  template = '',
  stock,
  externalUrl,
  button,
  sellerInfo,
  brand,
  isMirakl,
  estimateDeliveryDate,
  stockAlert = true,
  customizableBundleOptions,
  bundledItems,
  woosbIds,
}: TypeProductDescriptionProps) => {
  let dateInterval = '';
  let isSellerClosed = false;

  if (sellerInfo?.closedFrom && sellerInfo.closedTo) {
    const closedFrom = new Date(sellerInfo.closedFrom);
    const closedTo = new Date(sellerInfo.closedTo);
    const now = new Date();

    closedFrom.setDate(closedFrom.getDate() + 1);
    closedTo.setDate(closedTo.getDate() + 1);

    if (now >= closedFrom && now <= closedTo) {
      dateInterval = formatDateInterval(closedFrom, closedTo);
      isSellerClosed = true;
    }
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex flex-col gap-2 py-4 lg:gap-6 lg:py-6">
        <div className="flex flex-col gap-3">
          <div className="flex justify-between">
            {category && categoryLink && (
              <NextLink href={categoryLink}>
                <a className="u-body u-body--s text-secondary-500">
                  {category}
                </a>
              </NextLink>
            )}
          </div>
          <h1 className="u-headline u-headline--h2">{name}</h1>
        </div>
        <div className="u-body u-body--s flex flex-col gap-2 text-typo-secondary">
          <ViewMore
            maxHeight={70}
            style="link"
            labels={{ viewLess: 'Ver menos', viewMore: 'Ver más' }}
          >
            {(isOpen) => (
              <div
                className={oneLine`
                [&>*:not(:last-child,li)]:mb-2
                [&>ol]:ml-5 [&>ol]:!list-decimal
                [&>ul]:ml-5 [&>ul]:!list-disc
                ${isOpen ? '' : 'line-clamp-3'}
              `}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </ViewMore>
          {button?.label && button.link.url && (
            <NextLink href={removeBaseURL(button?.link.url ?? '')} passHref>
              formik
              <Button
                label={button?.label as string}
                negative={button?.negativo}
                as="a"
                style={button?.style ?? 'primary'}
                size={button?.size}
                className="min-w-full md:min-w-fit"
                {...(button?.showIcon && {
                  icon: <Info />,
                })}
              />
            </NextLink>
          )}
        </div>
      </div>

      {customizableBundleOptions?.isCustomizable && (
        <BundleOptions
          onCardAddProduct={customizableBundleOptions.onCardAddProduct}
        />
      )}

      <ProductForms
        type={type}
        bundledItems={bundledItems}
        woosbIds={woosbIds}
        stock={stock}
        template={template}
        price={price}
        productId={productId}
        regularPrice={regularPrice}
        moreInfo={moreInfo}
        isMirakl={isMirakl}
        externalUrl={externalUrl}
        stockAlert={stockAlert}
        attributes={attributes}
        variations={variations}
        isSellerClosed={isSellerClosed}
      />

      <div className="flex flex-col gap-2">
        {stock && isOutOfStock(stock) ? (
          <Notifications
            message={stockAlert ? 'Agotado temporalmente' : 'Agotado'}
            state="ERROR"
            box={false}
            Icon={Info}
          />
        ) : null}

        <Notifications
          message="La imagen mostrada puede diferir ligeramente del producto real."
          state="INFO"
          box={false}
          className="hidden lg:inline"
        />
      </div>

      {sellerInfo && (
        <SellerBox
          seller={sellerInfo.name}
          sellerLink={
            isMirakl ? routes.sellers.seller(sellerInfo.slug) : routes.about
          }
          {...(brand && {
            brand,
          })}
          {...(estimateDeliveryDate?.estimateDeliveryDateMax ||
          estimateDeliveryDate?.estimateDeliveryDateMin
            ? {
                deliveryDays: `${
                  estimateDeliveryDate.estimateDeliveryDateMin ?? ''
                } ${
                  estimateDeliveryDate.estimateDeliveryDateMax &&
                  estimateDeliveryDate.estimateDeliveryDateMin
                    ? '-'
                    : ''
                } ${
                  estimateDeliveryDate.estimateDeliveryDateMax ?? ''
                } días laborables`,
              }
            : {
                ...((sellerInfo?.maxTime || sellerInfo?.minTime) && {
                  deliveryDays: `${sellerInfo.minTime ?? ''} ${
                    sellerInfo.minTime && sellerInfo.maxTime ? '-' : ''
                  } ${sellerInfo.maxTime ?? ''} días laborables`,
                }),
              })}
          {...((sellerInfo?.minPrice || sellerInfo?.maxPrice) && {
            deliveryCosts:
              sellerInfo.minPrice === sellerInfo.maxPrice
                ? `${sellerInfo.minPrice} €`
                : oneLine`
                    ${sellerInfo.minPrice ? sellerInfo.minPrice : ''} 
                    ${sellerInfo.minPrice && sellerInfo.maxPrice ? '-' : ''} 
                    ${sellerInfo.maxPrice ? sellerInfo.maxPrice : ''} €
                  `,
          })}
          // TODO: Remove this when the backend return null for freeShippingAmount instead of 0 when the seller doesn't have free shipping
          {...(sellerInfo.freeShippingAmount &&
            sellerInfo.freeShippingAmount !== '0' && {
              minFreeShipping: `${sellerInfo.freeShippingAmount} €`,
            })}
          alwaysFree={sellerInfo.alwaysFree || false}
          className="mt-4"
        >
          {isSellerClosed && (
            <Notifications
              state="ERROR"
              box
              Icon={Info}
              message={`Lo sentimos. Este vendedor no suministra productos desde el ${dateInterval}`}
            />
          )}
          {sellerInfo?.suspended && (
            <Notifications
              state="ERROR"
              box
              Icon={Info}
              message="Lo sentimos. Este vendedor no suministra productos actualmente"
            />
          )}
        </SellerBox>
      )}
    </div>
  );
};
